import { action, observable, runInAction } from 'mobx';
import controller from './controller';
import {
  LegalListItem,
  CreateLegalItemInterface, UpdateLegalItemInterface,
  PartnerLegalFile,
} from './partnerLegalApi';
import { ErrorInterface } from 'modules/core/api/globalApi';

export interface PartnerLegalStore {
  legalList: LegalListItem[],
  legalListByPartnerId?: string | null,
  loading: boolean,
  currentPartnerLegal: LegalListItem | null; // текущее юр.лицо - которое редактируется в модальной форме или в мастере подготовки данных
  error: ErrorInterface | null,

  // Изолированные поля для PartnerLegalSelector. Не пересекаются с legalList/loading,
  // которые используют панель/модалка/InvoicesList.
  // selectorListByPartnerId: id партнёра, по которому загружен selectorList;
  //   null - загружен общий список (все юр.лица); undefined - ещё не загружали.
  selectorList: LegalListItem[],
  selectorListByPartnerId: string | null | undefined,
  selectorLoading: boolean,
  selectorError: ErrorInterface | null,

  // Файлы юр.лица. Используются вкладкой "Файлы" в PartnerLegalModal.
  // filesByLegalId — id юр.лица, для которого загружены files (для cache-инвалидации).
  files: PartnerLegalFile[],
  filesByLegalId: string | null | undefined,
  filesShowDeleted: boolean,
  filesLoading: boolean,
  filesError: ErrorInterface | null,
}

export const partnerLegalStore = observable({
  legalList: [],
  legalListByPartnerId: undefined,
  loading: false,
  currentPartnerLegal: null,
  error: null,

  selectorList: [],
  selectorListByPartnerId: undefined,
  selectorLoading: false,
  selectorError: null,

  files: [],
  filesByLegalId: undefined,
  filesShowDeleted: false,
  filesLoading: false,
  filesError: null,
} as PartnerLegalStore);

const buildErrorFromResponse = (response: any): ErrorInterface => {
  const { errorData } = response;
  return {
    message: errorData?.message,
    errorCode: errorData?.errorCode,
    details: errorData?.details,
    timestamp: errorData?.timestamp,
    status: response?.status,
    connectError: response?.connectError,
    isError: response?.error,
  };
};

export const partnerLegalActions = {

  handleError: (response) => {
    if (response.errorData) {
      runInAction(() => {
        partnerLegalStore.error = buildErrorFromResponse(response);
      });
      throw response.errorData;
    }
  },

  clearError: action(() => {
    partnerLegalStore.error = null;
  }),

  clearLoading: action(() => {
    partnerLegalStore.loading = false;
  }),

  setLoading: action(() => {
    partnerLegalStore.loading = true;
  }),

  setLegalListByPartnerId: action((id: string | null | undefined) => {
    partnerLegalStore.legalListByPartnerId = id;
  }),

  setCurrentPartnerLegal: action((item: LegalListItem | null) => {
    partnerLegalStore.currentPartnerLegal = item;
  }),

  clearCurrentPartnerLegal: action(() => {
    partnerLegalStore.currentPartnerLegal = null;
  }),

  getAllPartnerLegals: async (): Promise<LegalListItem[]> => {
    runInAction(() => {
      partnerLegalStore.loading = true;
      partnerLegalStore.legalListByPartnerId = null;
      partnerLegalStore.error = null;
    });
    const response = await controller.getPartnerLegalList();
    runInAction(() => {
      if (response.errorData) {
        partnerLegalStore.error = buildErrorFromResponse(response);
      } else {
        partnerLegalStore.legalList = response.data ?? [];
      }
      partnerLegalStore.loading = false;
    });
    return response.errorData ? [] : (response.data ?? []);
  },

  getPartnerLegalByPartnerId: async (partnerId: string): Promise<LegalListItem[]> => {
    runInAction(() => {
      partnerLegalStore.loading = true;
      partnerLegalStore.legalListByPartnerId = partnerId;
      partnerLegalStore.error = null;
    });
    const response = await controller.getPartnerLegalByPartnerId(partnerId);
    runInAction(() => {
      if (response.errorData) {
        partnerLegalStore.error = buildErrorFromResponse(response);
        partnerLegalStore.legalListByPartnerId = null;
      } else {
        partnerLegalStore.legalList = response.data ?? [];
      }
      partnerLegalStore.loading = false;
    });
    return response.errorData ? [] : (response.data ?? []);
  },

  /**
   * Изолированная загрузка списка для PartnerLegalSelector.
   * Пишет только в selectorList/selectorLoading/selectorError, не трогая legalList/loading.
   *
   * - partnerId задан                              -> грузим юр.лица партнёра
   * - partnerId не задан, onlyOwnedToPartner=false -> грузим общий список
   * - partnerId не задан, onlyOwnedToPartner=true  -> ничего не грузим, очищаем список
   */
  getPartnerLegalsForSelector: async (
    partnerId?: string,
    onlyOwnedToPartner?: boolean
  ): Promise<LegalListItem[]> => {
    runInAction(() => {
      partnerLegalStore.selectorError = null;
      partnerLegalStore.selectorLoading = true;
      partnerLegalStore.selectorListByPartnerId = partnerId ?? null;
    });

    if (!partnerId && onlyOwnedToPartner) {
      runInAction(() => {
        partnerLegalStore.selectorList = [];
        partnerLegalStore.selectorLoading = false;
      });
      return [];
    }

    const response = partnerId
      ? await controller.getPartnerLegalByPartnerId(partnerId)
      : await controller.getPartnerLegalList();

    runInAction(() => {
      if (response.errorData) {
        partnerLegalStore.selectorError = buildErrorFromResponse(response);
        partnerLegalStore.selectorList = [];
        partnerLegalStore.selectorListByPartnerId = undefined;
      } else {
        partnerLegalStore.selectorList = response.data ?? [];
      }
      partnerLegalStore.selectorLoading = false;
    });

    return response.errorData ? [] : (response.data ?? []);
  },

  // ==== Файлы юр.лица ====

  setFilesShowDeleted: action((value: boolean) => {
    partnerLegalStore.filesShowDeleted = value;
  }),

  clearPartnerLegalFiles: action(() => {
    partnerLegalStore.files = [];
    partnerLegalStore.filesByLegalId = undefined;
    partnerLegalStore.filesError = null;
  }),

  getPartnerLegalFiles: async (
    partnerLegalId: string,
    showDeleted: boolean = false
  ): Promise<PartnerLegalFile[]> => {
    runInAction(() => {
      partnerLegalStore.filesLoading = true;
      partnerLegalStore.filesByLegalId = partnerLegalId;
      partnerLegalStore.filesShowDeleted = showDeleted;
      partnerLegalStore.filesError = null;
    });
    const response = await controller.getPartnerLegalFiles({ partnerLegalId, showDeleted });
    runInAction(() => {
      if (response.errorData) {
        partnerLegalStore.filesError = buildErrorFromResponse(response);
        partnerLegalStore.files = [];
      } else {
        partnerLegalStore.files = response.data ?? [];
      }
      partnerLegalStore.filesLoading = false;
    });
    return response.errorData ? [] : (response.data ?? []);
  },

  addPartnerLegalFile: async (
    partnerLegalId: string,
    file: File
  ): Promise<void> => {
    partnerLegalActions.clearError();
    const response = await controller.addPartnerLegalFile({ partnerLegalId, file });
    partnerLegalActions.handleError(response);
    // Перечитываем список после успешной загрузки.
    await partnerLegalActions.getPartnerLegalFiles(
      partnerLegalId,
      partnerLegalStore.filesShowDeleted
    );
  },

  deletePartnerLegalFile: async (fileId: string): Promise<void> => {
    partnerLegalActions.clearError();
    const response = await controller.deletePartnerLegalFile({ fileId });
    partnerLegalActions.handleError(response);
    // Если есть текущая привязка к юр.лицу — перечитываем список.
    if (partnerLegalStore.filesByLegalId) {
      await partnerLegalActions.getPartnerLegalFiles(
        partnerLegalStore.filesByLegalId,
        partnerLegalStore.filesShowDeleted
      );
    }
  },

  createPartnerLegal: async (request: CreateLegalItemInterface): Promise<LegalListItem> => {
    partnerLegalActions.clearError();
    const response = await controller.createPartnerLegal(request);
    partnerLegalActions.handleError(response);
    runInAction(() => {
      partnerLegalStore.legalList = [...partnerLegalStore.legalList, response.data];
      partnerLegalStore.currentPartnerLegal = response.data;
    });
    return response.data;
  },

  updatePartnerLegal: async (request: UpdateLegalItemInterface): Promise<LegalListItem> => {
    partnerLegalActions.clearError();
    const response = await controller.updatePartnerLegal(request);
    partnerLegalActions.handleError(response);
    runInAction(() => {
      partnerLegalStore.legalList = partnerLegalStore.legalList
        .map((v) => (v.id === request.id ? response.data : v));
      if (partnerLegalStore.currentPartnerLegal?.id === request.id) {
        partnerLegalStore.currentPartnerLegal = response.data;
      }
    });
    return response.data;
  },

  deletePartnerLegal: async (id: string): Promise<void> => {
    partnerLegalActions.clearError();
    const response = await controller.deletePartnerLegal(id);
    partnerLegalActions.handleError(response);
    runInAction(() => {
      partnerLegalStore.legalList = partnerLegalStore.legalList.filter((v) => v.id !== id);
      if (partnerLegalStore.currentPartnerLegal?.id === id) {
        partnerLegalStore.currentPartnerLegal = null;
      }
    });
  },

  // регистрация партнёра в Т-Банке для мультиплатежей
  startRegister: async (patnerLegalId: string): Promise<void> => {
    partnerLegalActions.clearError();
    const response = await controller.startRegister(patnerLegalId);
    partnerLegalActions.handleError(response);
    // return response.data;
  },

};
